import React, { Component, ErrorInfo, ReactNode } from 'react';
import DefaultButtonV2 from 'components/buttonV2/defaultButtonV2';
import { withTranslation, WithTranslation } from 'react-i18next';

interface Props extends WithTranslation {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({ errorInfo });
        /* eslint-disable no-console */
        console.error('ErrorBoundary caught:', error, errorInfo);
    }

    render() {
        const { t } = this.props;

        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6 font-sans">
                    <div className="bg-white border border-gray-200 rounded-xl p-10 max-w-lg w-full text-center shadow-sm">
                        <div className="mb-4 text-5xl">⚠</div>

                        <h1 className="text-xl font-semibold text-gray-900 mb-2">
                            {t('somethingWentWrong')}
                        </h1>
                        <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                            {t('unexpectedErrorOccurred')}
                        </p>

                        {this.state.error && (
                            <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 mb-4 text-left">
                                <p className="text-xs font-mono text-red-700 break-words m-0">
                                    {this.state.error.name}:{' '}
                                    {this.state.error.message}
                                </p>
                            </div>
                        )}

                        {this.state.errorInfo && (
                            <details className="text-left mb-6" open>
                                <summary className="text-xs text-gray-400 cursor-pointer mb-2 select-none">
                                    {t('stackTrace')}{' '}
                                </summary>
                                <pre className="text-xs text-gray-600 bg-gray-100 border border-gray-200 rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                                    {this.state.errorInfo.componentStack}
                                </pre>
                            </details>
                        )}

                        <div className="flex items-center justify-center gap-3">
                            <DefaultButtonV2
                                buttonText={t('reloadPage')}
                                bgColor="blue"
                                textColor="white"
                                Request={true}
                                onClick={() => window.location.reload()}
                            />
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default withTranslation()(ErrorBoundary);
